import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { zPosterElement, Poster, type PosterElement } from '@postmaker/poster';
import { api } from '../lib/api.ts';
import { guardarSeleccion, leerSeleccion } from '../lib/catalogo.ts';
import { resueltoDeMuestra } from '../lib/muestra.ts';
import { Cargando, Chip, EncabezadoPagina, Insignia } from '../componentes/ui.tsx';
import { PasosPost } from '../componentes/PasosPost.tsx';
import { BannerCampania } from '../componentes/BannerCampania.tsx';

interface PlantillaCatalogo {
  id: string;
  nombre: string;
  tipoContenido: string;
  numProductos: number;
  formatos: string[];
}

interface PlantillaDetalle {
  lienzos: { formato: 'R4_5' | 'R1_1' | 'R9_16'; fondoColor: string | null; fondoUrl: string | null; elementos: unknown[] }[];
  assets: Record<string, string>;
}

/** PASO 1 (D12): el cliente elige el diseño VIENDO cómo queda, antes que los productos. */
export function PaginaPlantillas() {
  const { t } = useTranslation();
  const navegar = useNavigate();
  const busqueda = useSearch({ strict: false }) as { tipoContenido?: string; marca?: string };
  const seleccion = leerSeleccion();
  const tipoContenido = busqueda.tipoContenido ?? seleccion?.tipoContenido ?? 'PROMOCION';

  // Paso 2: se muestran TODOS los diseños publicados (el tipo lo decide la plantilla).
  const plantillas = useQuery({
    queryKey: ['catalogo-plantillas', busqueda.marca ?? ''],
    queryFn: () =>
      api<{ items: PlantillaCatalogo[] }>(
        `/catalogo/plantillas${busqueda.marca ? `?marca=${busqueda.marca}` : ''}`,
      ),
  });

  /** Filtro por espacios de producto: 0 = todos, 1/2/3 pares. */
  const [filtroPares, setFiltroPares] = useState(0);

  // Con productos ya elegidos (p. ej. "Armar post" del inicio) la plantilla salta directo a generar.
  const productosPrevios = seleccion?.productoIds ?? [];

  // Paso 2 exige el paso 1 hecho: sin productos elegidos se vuelve al inicio del flujo.
  useEffect(() => {
    if (productosPrevios.length === 0) {
      void navegar({ to: '/marca', search: { tipoContenido } as never, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productosPrevios.length]);

  function elegir(p: PlantillaCatalogo) {
    guardarSeleccion({
      productoIds: productosPrevios,
      tipoContenido: p.tipoContenido,
      plantillaId: p.id,
      plantillaNumProductos: p.numProductos,
    });
    void navegar({ to: '/editor', search: { plantillaId: p.id } });
  }

  const visibles = (filtradas: PlantillaCatalogo[]) =>
    filtroPares === 0 ? filtradas : filtradas.filter((p) => p.numProductos === filtroPares);

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      {/* El flujo manda: paso 2 grande arriba */}
      <div className="flex justify-center py-2">
        <PasosPost paso={2} />
      </div>
      <EncabezadoPagina
        titulo={t('plantillas.eligeDiseno')}
        subtitulo={t('plantillas.eligeDisenoSub')}
        acciones={
          <div className="flex flex-wrap gap-2">
            {([0, 1, 2, 3] as const).map((n) => (
              <Chip key={n} activo={filtroPares === n} onClick={() => setFiltroPares(n)}>
                {n === 0 ? t('plantillas.todas') : t('plantillas.pares', { n })}
              </Chip>
            ))}
          </div>
        }
      />

      <BannerCampania seccion="plantillas_h" />

      {plantillas.isPending ? <Cargando texto={t('comun.cargando')} /> : null}

      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
        {visibles(plantillas.data?.items ?? []).map((p) => (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => elegir(p)}
              className="tarjeta w-full p-3 text-left transition hover:-translate-y-0.5 hover:shadow-[var(--sombra-alta)]"
            >
              <MiniPlantilla plantillaId={p.id} />
              <div className="mt-2.5 flex items-center justify-between gap-2">
                <p className="truncate text-[15px] font-medium">{p.nombre}</p>
                <Insignia>{t('plantillas.espacios', { n: p.numProductos })}</Insignia>
              </div>
              {productosPrevios.length > 0 ? (
                <p className="text-xs text-suave">
                  {t('plantillas.postsEstimados', { n: Math.ceil(productosPrevios.length / p.numProductos) })}
                </p>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
      {visibles(plantillas.data?.items ?? []).length === 0 && !plantillas.isPending ? (
        <p className="py-10 text-center text-sm text-suave">{t('plantillas.vacio')}</p>
      ) : null}
    </main>
  );
}

/** Miniatura REAL del diseño: el mismo motor de arte con datos de muestra (regla 6). */
function MiniPlantilla({ plantillaId }: { plantillaId: string }) {
  const detalle = useQuery({
    queryKey: ['catalogo-plantilla', plantillaId],
    queryFn: () => api<PlantillaDetalle>(`/catalogo/plantillas/${plantillaId}`),
    staleTime: 60_000,
  });

  const lienzo =
    detalle.data?.lienzos.find((l) => l.formato === 'R4_5') ?? detalle.data?.lienzos[0] ?? null;

  if (!lienzo) {
    return <div className="interno aspect-[4/5] animate-pulse" />;
  }

  let elementos: PosterElement[] = [];
  try {
    elementos = zPosterElement.array().parse(lienzo.elementos);
  } catch {
    // lienzo corrupto: mejor miniatura vacía que romper el paso 1
  }

  const alto = lienzo.formato === 'R9_16' ? 1920 : lienzo.formato === 'R1_1' ? 1080 : 1350;

  return (
    <MiniEscalado ancho={1080} alto={alto}>
      <Poster
        resuelto={resueltoDeMuestra({
          formato: lienzo.formato,
          elementos,
          fondoColor: lienzo.fondoColor,
          fondoUrl: lienzo.fondoUrl,
          assets: detalle.data?.assets ?? {},
        })}
      />
    </MiniEscalado>
  );
}

/** Mide el ancho real de la tarjeta y escala el lienzo de 1080 px para que quepa exacto. */
function MiniEscalado({ ancho, alto, children }: { ancho: number; alto: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(0);

  useLayoutEffect(() => {
    const nodo = ref.current;
    if (!nodo) return;
    const medir = () => setEscala(nodo.clientWidth / ancho);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [ancho]);

  return (
    <div ref={ref} className="interno relative w-full overflow-hidden" style={{ aspectRatio: `${ancho} / ${alto}` }}>
      {escala > 0 ? (
        <div className="pointer-events-none absolute left-0 top-0" style={{ transform: `scale(${escala})`, transformOrigin: 'top left' }}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
